/**
 * @author: Rejo Varghese
 * @description: This is the form component for the buses component
 */

import { Form, Input, Button, Select, Typography, Layout, Row, Col, Modal, Switch, message } from 'antd';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { isValid, isValidIndex, isValidArray } from '../../utils/utilities';
import { showConfirm } from '../custom-components/custom-component';
import { BusesSeatLayoutRoot } from './buses-seat-layout-root';
import './buses-component-form.scss';
import { getSeatLayOut } from '../../actions/admin.action';
import { createAgentBusAction, updateAgentBusAction, validateSeatLayoutAction, getBusTemplatesAction, fetchSeatLayoutByTemplateIdAction } from '../../actions/vechicle-actions';
import { getDescriptionName } from './buses-component-module';
import { CloneBusComponent } from './clone-bus-component/clone-bus.component';


const { Content } = Layout;
const { Title } = Typography;
const { Option } = Select;

export const BusesComponentForm = (props) => {
    const {
        formData,
        updateFormData,
        busesData,
        fetchBuses,
        seatGroups,
        seatTypes,
        coachTypes
    } = {...props};

    const [form] = Form.useForm();
    const [seatLayoutVisible, setSeatLayoutVisible] = useState(false);
    const [fetchedSeatLayout, setFetchedSeatLayout] = useState([]);
    const [formattedSeatLayout, setFormattedSeatLayout] = useState([]);
    const [resetTrigger, setResetTrigger] = useState(false);
    const [busData, setBusData] = useState({});
    const [disableDefaultFare, setDisableDefaultFare] = useState(true);
    const [busTemplates, setBusTemplates] = useState([]);
    const [templateId, setTemplateId] = useState(null);
    const [seatLayoutEdited, setSeatLayoutEdited] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        getBusTemplatesAction((data) => {
            setBusTemplates(data || []);
        }, () => {});
    }, []);

    const onSuccess = () => {
        fetchBuses();
        onReset();
    }

    const onReset = () => {
        updateFormData({});
        setFormattedSeatLayout([]);
        setFetchedSeatLayout([]);
        setSeatLayoutVisible(false);
        setBusData({});
        setTemplateId(null);
        setSeatLayoutEdited(false);
        form.resetFields();
        setResetTrigger(prev => !prev);
    }

    const handleModalClose = () => {
        setSeatLayoutVisible(false);
    };

    const validateSeats = () => {
        if (isValid(formData) && formData.id) {
            validateSeatLayoutAction(formData.id, (data) => {
            }, () => {
            });
        }
    }

    const formatSeatLayout = (seatLayout) => {
        let seatLayoutData = [];
        if (isValid(seatLayout) && Array.isArray(seatLayout)) {
            let tempDefaultFare = false;
            seatLayout.forEach(seat => {
                let isEmptySeat = seat.seat_number.toLowerCase().includes('empty');
                let seatObject = {
                    seat_number: seat.seat_number || '',
                    adjacent_seat_number: seat.adjacent_seat_number || '',
                    row: parseInt(seat.seat_row_number) || 1,
                    column: parseInt(seat.seat_column_number) || 1,
                    seat_type: isEmptySeat ? 'empty' : seat.sleeper ? 'sleeper' : 'seater',
                    seat_group: seat.seat_group_id || null,
                    upper_deck: parseInt(seat.upper_deck) || 0,
                    seat_orientation: [270,-270].includes(seat.seat_orientation) ? 'landscape' : 'portrait',
                    seat_feature: seat.seat_feature || null,
                    seat_feature_size: seat.sleeper ? 'sleeper' : 'seater',
                    feature_type: seat.feature_type || null
                };
                if (!isValid(seat.seat_group_id) && seat.seat_number != "Empty"){
                    tempDefaultFare = true;
                }
                seatLayoutData.push(seatObject);
            });
            setDisableDefaultFare(tempDefaultFare);
        }
        return seatLayoutData;
    }

    const onTemplateChange = (value) => {
        setTemplateId(value);
        setSeatLayoutEdited(false);
        setBusData({});
        setFormattedSeatLayout([]);
        fetchSeatLayoutByTemplateIdAction(value, (seatLayout) => {
            const formatted = formatSeatLayout(seatLayout);
            setFormattedSeatLayout(formatted);
            setSeatLayoutVisible(true);
        }, () => {
            message.error('Failed to load template seat layout');
        });
    };

    const compareAndReturnUpdatedSeatLayout = (seatLayoutData) => {
        let newSeatLayoutObject = {}, oldSeatLayoutObject = {}, oldSeatLayoutKey = [], newSeatLayoutKey = [], updatedSeatLayout = {};
        seatLayoutData.forEach(seat => {
            let key = `${seat.seat_row_number}-${seat.seat_column_number}-${seat.upper_deck}`;
            newSeatLayoutObject[key] = seat;
            newSeatLayoutKey.push(key);
        });
        fetchedSeatLayout.forEach(seat => {
            let key = `${seat.seat_row_number}-${seat.seat_column_number}-${seat.upper_deck}`;
            oldSeatLayoutObject[key] = seat;
            oldSeatLayoutKey.push(key);
        });

        let uniqueSeatLayoutKeys = [...new Set([...oldSeatLayoutKey, ...newSeatLayoutKey])];

        let validationKeys = ["seat_number", "adjacent_seat_number", "seat_group_id", "sleeper", "window_seat", "seat_column_number", "seat_row_number", "seat_orientation", "upper_deck", "seat_feature", "feature_type"];

        uniqueSeatLayoutKeys.forEach(key => {
            let oldSeat = oldSeatLayoutObject[key];
            let newSeat = newSeatLayoutObject[key];
            if (isValid(oldSeat) && isValid(newSeat)) {
                validationKeys.forEach((validationKey, index) => {
                    if (oldSeat[validationKey] != newSeat[validationKey]) {
                        if (isValid(updatedSeatLayout[oldSeat.id])) {
                            updatedSeatLayout[oldSeat.id][validationKey] = newSeat[validationKey];
                        } else {
                            updatedSeatLayout[oldSeat.id] = {};
                            updatedSeatLayout[oldSeat.id][validationKey] = newSeat[validationKey];
                        }
                    }
                });
            }
            else {
                if (!isValid(oldSeat)) {
                    if (isValid(updatedSeatLayout['new'])) {
                        updatedSeatLayout['new'].push(newSeat);
                    } else {
                        updatedSeatLayout['new'] = [newSeat];
                    }
                }
                if (!isValid(newSeat)) {
                    updatedSeatLayout[oldSeat.id] = {};
                    updatedSeatLayout[oldSeat.id]['is_active'] = false;
                }
            }
        });
        return updatedSeatLayout;
    }

    const submitSeatLayoutChange = (seatLayout) => {
        if (isValid(formData) && formData.id) {
            let busDetails = {
                id: formData.id
            }
            let oldAssignedSeatGroups = isValid(formData.seat_group_ids) ? formData.seat_group_ids.includes(',') ? formData.seat_group_ids.split(',') : [formData.seat_group_ids] : [];
            let newAssignedSeatGroups = seatLayout.seatGroupsAssigned;

            const oldAssignedSeatGroupsMatch = oldAssignedSeatGroups.length === newAssignedSeatGroups.length &&
                oldAssignedSeatGroups.every((element) => newAssignedSeatGroups.includes(element));

            if (!oldAssignedSeatGroupsMatch) {
                busDetails.seat_group_ids = newAssignedSeatGroups.join(',');
            }

            if (formData.extra_seats_in_last_row != seatLayout.extraSeatsInLastRow) {
                busDetails.extra_seats_in_last_row = seatLayout.extraSeatsInLastRow;
            }
            if (formData.number_of_rows !== seatLayout.lowerDeckRowColCount.num_rows) {
                busDetails.number_of_rows = seatLayout.lowerDeckRowColCount.num_rows;
            }
            if (formData.number_of_columns !== seatLayout.lowerDeckRowColCount.num_cols) {
                busDetails.number_of_columns = seatLayout.lowerDeckRowColCount.num_cols;
            }
            if (formData.number_of_upper_rows !== seatLayout.upperDeckRowColCount.num_rows) {
                busDetails.number_of_upper_rows = seatLayout.upperDeckRowColCount.num_rows;
            }
            if (formData.number_of_upper_columns !== seatLayout.upperDeckRowColCount.num_cols) {
                busDetails.number_of_upper_columns = seatLayout.upperDeckRowColCount.num_cols;
            }
            if (formData.number_of_seats !== seatLayout.totalSeatCount) {
                busDetails.number_of_seats = seatLayout.totalSeatCount;
            }
            if (formData.number_of_levels !== seatLayout.totalLevels) {
                busDetails.number_of_levels = seatLayout.totalLevels;
            }

            let updatedSeatLayout = compareAndReturnUpdatedSeatLayout(seatLayout.seatList);

            let tempBusData = {
                bus_details: busDetails,
                seat_layout: updatedSeatLayout,
                isAllSeatGroupsAssigned: seatLayout.isAllSeatGroupsAssigned
            }

            setBusData(tempBusData);
            handleModalClose();
        }
        else {
            let tempBusData = {
                bus_details: {
                    extra_seats_in_last_row: seatLayout.extraSeatsInLastRow,
                    number_of_rows: seatLayout.lowerDeckRowColCount.num_rows,
                    number_of_columns: seatLayout.lowerDeckRowColCount.num_cols,
                    number_of_upper_rows: seatLayout.upperDeckRowColCount.num_rows,
                    number_of_upper_columns: seatLayout.upperDeckRowColCount.num_cols,
                    number_of_seats: seatLayout.totalSeatCount,
                    number_of_levels: seatLayout.totalLevels,
                    travels_id: global.travelsId,
                    seat_group_ids: seatLayout.seatGroupsAssigned.join(',')
                },
                seat_layout: seatLayout.seatList,
                isAllSeatGroupsAssigned: seatLayout.isAllSeatGroupsAssigned
            }
            setBusData(tempBusData);
            setSeatLayoutEdited(true);
            handleModalClose();
        }
    }


    useEffect(() => {
        if (isValid(formData) && formData.id) {
            getSeatLayOut(formData.id, (data) => {
                let seatLayout = data.seat_layout;
                setFetchedSeatLayout(seatLayout);
                setFormattedSeatLayout(formatSeatLayout(seatLayout));
            }, () => {});
            form.setFieldsValue({
                name: formData.name,
                description: formData.description,
                isAc: formData.isAc,
                supported_fare: formData.supported_fare,
                extra_seats_in_last_row: formData.extra_seats_in_last_row,
                coach_type: formData.coach_type,
                seat_type: formData.seat_type
            });
        } else {
            // Reset seat layout data when formData is empty
            setFetchedSeatLayout([]);
            setFormattedSeatLayout([]);
            setTemplateId(null);
            setSeatLayoutEdited(false);
            form.resetFields();
        }
    }, [formData]);

    const onFinish = (values) => {
        if (isValid(formData) && formData.id) {
            // Compare form values with formData
            const formValues = {
                name: values.name,
                description: values.description,
                isAc: values.isAc,
                supported_fare: values.supported_fare,
                extra_seats_in_last_row: values.extra_seats_in_last_row,
                coach_type: values.coach_type,
                seat_type: values.seat_type
            };

            const formDataValues = {
                name: formData.name,
                description: formData.description,
                isAc: formData.isAc,
                supported_fare: formData.supported_fare,
                coach_type: formData.coach_type,
                seat_type: formData.seat_type
            };

            // Create updatedFormData object with only changed values
            let updatedFormData = {
                bus_details: {id: formData.id},
                seat_layout: {},
                operatorCode:global.operatorCode,
                travelsId:global.travelsId,
            };

            if (values.supported_fare && busData.isAllSeatGroupsAssigned){
                updatedFormData.bus_details.supported_fare = false;
            }
            if (isValid(busData) && isValid(busData.bus_details)) {
                updatedFormData.bus_details = {...updatedFormData.bus_details, ...busData.bus_details};
            }

            if (isValid(busData) && isValid(busData.seat_layout)) {
                updatedFormData.seat_layout = {...busData.seat_layout};
            }

            // Check each field and add to updatedFormData only if changed
            Object.keys(formValues).forEach(key => {
                if (formValues[key] !== formDataValues[key]) {
                    updatedFormData.bus_details[key] = formValues[key];
                }
            });

            if (Object.keys(updatedFormData.bus_details).length > 1 || Object.keys(updatedFormData.seat_layout).length > 0) {
                // Handle form submission for updated values
                updateAgentBusAction(updatedFormData, () => {
                    message.success('Bus updated successfully!');
                    onSuccess();
                }, () => {
                    message.error('Failed to update bus!');
                });
            } else {
                message.error('No changes detected in form values');
            }
        } else {
            if (isValid(busData) && isValid(busData.bus_details) && isValid(busData.seat_layout)) {
                let insertedFormData = {
                    bus_details: busData.bus_details,
                    seat_layout: busData.seat_layout,
                    operatorCode:global.operatorCode,
                    travelsId:global.travelsId,
                }

                if (values.supported_fare && busData.isAllSeatGroupsAssigned){
                    insertedFormData.bus_details.supported_fare = false;
                }
                insertedFormData.bus_details = {...insertedFormData.bus_details, ...values};
                createAgentBusAction(insertedFormData, () => {
                    message.success('Bus created successfully!');
                    onSuccess();
                }, () => {
                    message.error('Failed to create bus!');
                });
            }
            else {
                message.error("Seat layout not created!");
            }
        }
    };

    const isEditingBus = isValid(formData) && !!formData.id;
    const seatLayoutGroups = [...new Set(formattedSeatLayout.map(seat => seat.seat_group).filter(Boolean))].join(', ');

    return (
        <Layout>
            <Title level={4}>Buses Component Form</Title>
            <Content style={{ margin: '10px' }} className='vechicle-content'>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={formData}
                    onValuesChange={(changedValues, allValues)=>{
                        if(isValid(changedValues.coach_type)||isValidIndex(changedValues.isAc)||isValid(changedValues.seat_type)||isValid(changedValues.name)){
                            let{
                                coach_type,
                                isAc,
                                seat_type,
                                name
                            }=allValues;
                            let description=getDescriptionName(coach_type,isAc,seat_type,name);

                            form.setFieldsValue({
                                    description:description
                                })
                        }
                    }}
                >
                    {!isEditingBus && (
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Create from Template">
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select a bus template to pre-fill seat layout"
                                        value={templateId}
                                        disabled={seatLayoutEdited}
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().includes(input.toLowerCase())
                                        }
                                        onChange={onTemplateChange}
                                        onClear={() => {
                                            setTemplateId(null);
                                            setFormattedSeatLayout([]);
                                            setBusData({});
                                            setSeatLayoutEdited(false);
                                        }}
                                    >
                                        {isValidArray(busTemplates) && busTemplates.map(t => (
                                            <Option key={t.id} value={t.id}>{t.template_name}</Option>
                                        ))}
                                    </Select>
                                    {seatLayoutEdited && (
                                        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                                            Template locked — seat layout has been edited. Reset to change template.
                                        </div>
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item
                                name="name"
                                label="Bus identifier"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="description"
                                label="Description"
                            >
                                <Input disabled/>
                            </Form.Item>
                        </Col>
                        <Col span={3}>
                            <Form.Item
                                name="isAc"
                                label="AC"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={3}>
                            <Form.Item
                                name="supported_fare"
                                label="Default Fare supported"
                                valuePropName="checked"
                            >
                                <Switch disabled={disableDefaultFare}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item
                                name="extra_seats_in_last_row"
                                label="Last row male reserved"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>


                       <Col span={8}>
                            <Form.Item
                                name="coach_type"
                                label="Coach Type"
                            >
                                <Select
                                    showSearch
                                    placeholder="Coach type"
                                >
                                    {isValidArray(coachTypes) && coachTypes.map(coachType => (
                                        <Option key={coachType.name} value={coachType.name}>{coachType.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                       </Col>
                       <Col span={8}>
                            <Form.Item
                                name="seat_type"
                                label="Seat Type"
                                rules={[{ required: true, message: 'Please select a seat type' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Seat type"
                                >
                                    {isValidArray(seatTypes) && seatTypes.map(seatType => (
                                        <Option key={seatType.name} value={seatType.name}>{seatType.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                       </Col>
                       <Col span={8}>
                            <Button onClick={() => setSeatLayoutVisible(true)} style={{ marginLeft: 8, marginTop: 20 }}>
                                Seat Layout
                            </Button>
                       </Col>
                    </Row>

                    <Row gutter={16} justify='center' style={{ marginTop: 20 }}>
                        <Col>
                            <Button onClick={() => validateSeats()}>
                                Validate Seats
                            </Button>
                        </Col>
                        <Col>
                            <CloneBusComponent
                                isAc={formData.isAc}
                                busId={formData.id}
                                getBusDetails={fetchBuses}
                            />
                        </Col>
                        <Col>
                            <Form.Item>
                                <Button type="primary" htmlType="submit">
                                    Submit
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item>
                                <Button onClick={onReset} style={{ marginLeft: 8 }}>
                                    Reset
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>

                <Modal
                    title={seatLayoutGroups ? `Seat Layout - ${seatLayoutGroups}` : 'Seat Layout'}
                    visible={seatLayoutVisible}
                    footer={null}
                    onOk={handleModalClose}
                    onCancel={handleModalClose}
                    maskClosable={false}
                    destroyOnClose={false}
                    width={1200}
                >
                    <BusesSeatLayoutRoot
                        seatGroups={seatGroups}
                        seatLayoutData={formattedSeatLayout}
                        resetTrigger={resetTrigger}
                        submitSeatLayoutChange={submitSeatLayoutChange}
                    />
                </Modal>
            </Content>
        </Layout>
    );
};
